import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import JsonLd, { buildBreadcrumbs } from "@/components/site/JsonLd";
import TrustBand from "@/components/site/TrustBand";
import PopularRoutes from "@/components/site/PopularRoutes";
import { Link } from "react-router-dom";
import { MapPin, Users, ArrowRight, CheckCircle2 } from "lucide-react";
import { whatsappHref } from "@/lib/format";

const DEPARTMENTS = [
  "Cundinamarca", "Boyacá", "Santander", "Norte de Santander", "Antioquia",
  "Tolima", "Huila", "Meta", "Casanare", "Caldas",
  "Quindío", "Risaralda", "Valle del Cauca", "Cauca", "Nariño",
  "Bolívar", "Atlántico", "Magdalena", "Cesar", "Córdoba",
];

const FLEET_BY_GROUP = [
  { size: "5 – 10 pax", vehicle: "Van Ejecutiva Premium", note: "Comodidad ejecutiva, AC y bodega XL", bg: "/servicio-004.png" },
  { size: "11 – 19 pax", vehicle: "Buseta Turística Sprinter", note: "Vidrios panorámicos, audio guía y AC dual", bg: "/servicio-001.jpg" },
  { size: "20 – 40 pax", vehicle: "Bus Turístico", note: "Baño a bordo, TV/Sonido y cinturones de 3 puntos", bg: "/servicio-002.jpg" },
];

export default function TransporteColombia() {
  return (
    <PageShell testid="page-transporte-colombia">
      <SEO
        title="Transporte Turístico por Colombia desde Bogotá | Líneas Doradas"
        description="Viajes turísticos e intermunicipales desde Bogotá hacia cualquier destino de Colombia. Vans y buses para grupos, con conductores certificados."
      />
      <JsonLd
        id="breadcrumb-jsonld"
        data={buildBreadcrumbs([
          { name: "Inicio", url: "https://www.transporteenvanesybuses.com/" },
          { name: "Servicios", url: "https://www.transporteenvanesybuses.com/servicios/transporte-turistico-colombia" },
          { name: "Transporte por Colombia", url: "https://www.transporteenvanesybuses.com/servicios/transporte-turistico-colombia" },
        ])}
      />
      <JsonLd
        id="service-jsonld"
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Transporte turístico por Colombia desde Bogotá",
          "serviceType": "Transporte intermunicipal y turístico",
          "provider": { "@id": "https://www.transporteenvanesybuses.com/#organization" },
          "areaServed": { "@type": "Country", "name": "Colombia" },
          "url": "https://www.transporteenvanesybuses.com/servicios/transporte-turistico-colombia",
          "description": "Viajes turísticos e intermunicipales desde Bogotá hacia cualquier destino de Colombia, en van, buseta o bus para grupos.",
        }}
      />

      <section className="relative pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="orb-gold w-[500px] h-[500px] right-[-140px] top-[80px] opacity-70" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
          <div>
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">Servicio · 02</div>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-[#F5EFE0]">
              Transporte turístico por <span className="italic text-gold-grad">Colombia</span>, desde Bogotá hacia cualquier destino
            </h1>
            <p className="text-[#F5EFE0]/70 mt-5 max-w-xl text-base md:text-lg leading-relaxed">
              Movilizamos grupos por los 32 departamentos del país en vehículos verificados y conductores certificados con experiencia en carretera nacional.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/cotizar" data-testid="cta-cotizar" className="btn-gold px-7 py-3 rounded-full text-sm">Cotizar ruta</Link>
              <a href={whatsappHref("573123616674", "Hola, quiero cotizar un viaje fuera de Bogotá")} target="_blank" rel="noreferrer" className="btn-ghost-gold px-7 py-3 rounded-full text-sm">
                WhatsApp directo
              </a>
            </div>
          </div>
          <img src="/mapa-hero.webp" alt="Cobertura Colombia" className="w-full max-w-[440px] mx-auto drop-shadow-[0_0_40px_rgba(212,175,55,0.3)]" />
        </div>
      </section>

      <section className="py-20 md:py-24 border-t border-[#3A3229]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-10">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4">Vehículo según tu grupo</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0]">Elige el vehículo ideal</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {FLEET_BY_GROUP.map((f) => (
              <div key={f.size} className="card-lux p-6 relative overflow-hidden isolate min-h-[220px]">
                <img
                  src={f.bg}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover opacity-25 -z-10"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 -z-10 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,9,8,0.55) 0%, rgba(10,9,8,0.88) 60%, rgba(10,9,8,0.95) 100%)" }}
                />
                <div className="flex items-center gap-2 text-[#E8C87A] text-xs uppercase tracking-widest mb-3">
                  <Users size={13} /> {f.size}
                </div>
                <div className="font-display text-2xl text-[#F5EFE0] mb-2">{f.vehicle}</div>
                <div className="text-sm text-[#F5EFE0]/70">{f.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PopularRoutes />

      <section className="py-20 md:py-24 border-t border-[#3A3229]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-8">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4"><MapPin size={12} className="inline mr-1 -mt-1" /> Cobertura</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0]">Los 32 departamentos, a un solo click</h2>
            <p className="text-[#F5EFE0]/60 mt-3 text-sm">Algunos de los territorios donde operamos con más frecuencia:</p>
          </div>
          <div className="flex flex-wrap gap-2 max-w-4xl">
            {DEPARTMENTS.map((d) => (
              <span key={d} className="px-3 py-1.5 rounded-full text-xs border border-[#D4AF37]/25 text-[#F5EFE0]/80 hover:border-[#D4AF37] hover:text-[#F5EFE0]"
                    style={{ transitionProperty: "border-color,color", transitionDuration: "200ms" }}>
                {d}
              </span>
            ))}
            <span className="px-3 py-1.5 rounded-full text-xs border border-[#D4AF37] text-[#E8C87A]">
              + los demás 12 departamentos bajo consulta
            </span>
          </div>
        </div>
      </section>

      <TrustBand />
    </PageShell>
  );
}
